import config from "@/config";
import { getCookie } from "@/services/generic.methods";
import { globalLogout } from "@/services/generic-logout-method";
import { DataClientError } from "../errors";
import store from "@/store";
import {
  startLoading,
  endLoading,
  showLoading,
} from "@/store/slices/loading/loadingSlice";

/**
 * FrontdoorInternalService
 *
 * A native fetch-based HTTP engine for the Frontdoor REST APIs.
 */

const getBaseUrl = () => config.frontdoorApiUrl || "";

const getAuthHeaders = () => {
  const token = getCookie("CRM-Auth");
  const companyKey = getCookie("companyKey");
  const headers = {
    "Content-Type": "application/json",
    "Accept": "application/json",
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  if (companyKey) {
    headers["companykey"] = companyKey;
  }
  
  return headers;
};

const startGlobalLoading = () => {
  store.dispatch(showLoading(true));
  store.dispatch(startLoading());
};

const stopGlobalLoading = () => {
  store.dispatch(endLoading());
  setTimeout(() => store.dispatch(showLoading(false)), 500);
};

const handleResponse = async (response) => {
  stopGlobalLoading();

  if (response.status === 401) {
    globalLogout(true);
    throw new DataClientError("frontdoor", 401, "Unauthorized — session expired.", null);
  }

  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;
    try {
      const errData = await response.json();
      errorMessage = errData?.message || errData?.title || errorMessage;
    } catch {
      // JSON parse failed for error body — use status text
    }
    throw new DataClientError("frontdoor", response.status, errorMessage, null);
  }

  // Handle empty responses (e.g. 204 No Content)
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return response.json();
  }
  return null;
};

const _request = async (method, resource, options = {}) => {
  let url;
  try {
    const baseUrl = getBaseUrl();
    if (!baseUrl) throw new Error("Frontdoor API base URL is not configured in config.js");
    
    const path = resource.startsWith('/') ? resource : `/${resource}`;
    url = new URL(path, baseUrl);
    
    if (options.params) {
      Object.entries(options.params).forEach(([k, v]) => {
        if (v !== undefined && v !== null) url.searchParams.append(k, v);
      });
    }
  } catch (err) {
    console.error("[FrontdoorInternalService] URL Construction Error:", err);
    throw new DataClientError("frontdoor", 400, `Invalid URL: ${err.message}`, err);
  }

  startGlobalLoading();
  
  try {
    const headers = { ...getAuthHeaders(), ...options.headers };
    if (options.isMultipart) {
      delete headers["Content-Type"];
    }

    const fetchOptions = {
      method,
      headers,
      mode: 'cors',
      credentials: 'same-origin',
      body: options.body ? (options.isMultipart ? options.body : JSON.stringify(options.body)) : undefined,
    };

    const response = await fetch(url.toString(), fetchOptions);
    return await handleResponse(response);
  } catch (error) {
    stopGlobalLoading();
    console.error(`[FrontdoorInternalService] FETCH Error (${method}):`, error);
    if (error instanceof DataClientError) throw error;
    throw new DataClientError("frontdoor", 500, error.message, error);
  }
};

const FrontdoorInternalService = {
  get(resource, params = {}, options = {}) {
    return _request("GET", resource, { params, ...options });
  },

  post(resource, data = {}) {
    return _request("POST", resource, { body: data });
  },

  put(resource, data = {}) {
    return _request("PUT", resource, { body: data });
  },

  delete(resource) {
    return _request("DELETE", resource);
  },

  postMultipart(resource, formData) {
    return _request("POST", resource, { body: formData, isMultipart: true });
  },

  putMultipart(resource, formData) {
    return _request("PUT", resource, { body: formData, isMultipart: true });
  },
};

export default FrontdoorInternalService;
